import React, { FC, ReactNode } from 'react';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';

/**
 * Per-metric CPT-conditioning class, mirrored from the view-api `Statistics.cptConditioning`
 * field. See CPT_CODE_CAPTURE_AND_GROUPING.md ("Per-metric CPT-conditioning classes").
 */
export type CptConditioningClass = 'membership' | 'single-procedure-population' | 'no-cpt';

const NO_CPT_EXPLANATION =
  'Not conditioned by CPT: this is a room-interval metric with no defensible per-code attribution.';

interface MetricCptConditioningProps {
  /** Conditioning class declared by the backend for this metric. */
  conditioning?: CptConditioningClass | string | null;
  /** Combined cases excluded from a single-procedure-population metric under an active CPT filter. */
  excludedCombinedCases?: number | null;
  /**
   * Eligible-population count for a single-procedure-population metric under an active CPT filter.
   * This is a shared upper bound across all single-procedure-population metrics, not any one
   * metric's true sample size: metrics further drop cases missing their own events or outside
   * min/max thresholds. Populated alongside `excludedCombinedCases`; null against an older backend
   * that lacks the field, in which case the caption falls back to excluded-only.
   */
  retainedCases?: number | null;
  /** Whether a CPT filter is currently active (non-empty code selection). */
  active: boolean;
  children: ReactNode;
}

const pluralizeExcluded = (n: number): string => `${n} combined case${n === 1 ? '' : 's'} excluded`;
const pluralizeEligible = (n: number): string => `${n} eligible single-procedure case${n === 1 ? '' : 's'}`;

/**
 * Pure caption text from the eligible / excluded counts alone (or `null` when neither is known),
 * independent of any conditioning class. Mirrors the documented contract: `<retainedCases> eligible
 * single-procedure cases; <excludedCombinedCases> combined cases excluded` when the eligible count
 * is present; excluded-only fallback when it is absent; eligible-only when excluded is absent.
 * `retainedCases` is a shared upper bound across the single-procedure-population metrics, not any one
 * metric's true sample size, so it is never presented as `n = `.
 *
 * Shared by the class-aware `cptConditioningCaption` (General-tab metric tiles) and by the
 * utilization-tab / patientJourney summaries, which source the counts from a TOTALS row that
 * carries no per-metric conditioning class. See CPT_CODE_CAPTURE_AND_GROUPING.md.
 */
export const eligibleExcludedCaption = (
  retainedCases: number | null | undefined,
  excludedCombinedCases: number | null | undefined
): string | null => {
  const hasRetained = retainedCases !== null && retainedCases !== undefined;
  const hasExcluded = excludedCombinedCases !== null && excludedCombinedCases !== undefined;

  if (!hasRetained && !hasExcluded) {
    return null;
  }

  const excludedCaption = hasExcluded ? pluralizeExcluded(excludedCombinedCases as number) : '';
  const eligibleCaption = hasRetained ? pluralizeEligible(retainedCases as number) : '';
  return hasRetained ? `${eligibleCaption}${excludedCaption ? `; ${excludedCaption}` : ''}` : excludedCaption;
};

/**
 * Pure derivation of the conditioning caption text (or `null` when no caption should render).
 * Only `single-procedure-population` metrics ever produce a caption here, and only while a CPT
 * filter is active; `no-cpt` and `membership` metrics render nothing (`no-cpt` gets a separate
 * explanatory note handled outside this helper). The count-to-text formatting is delegated to
 * `eligibleExcludedCaption`.
 */
export const cptConditioningCaption = (
  active: boolean,
  conditioning: CptConditioningClass | string | null | undefined,
  retainedCases: number | null | undefined,
  excludedCombinedCases: number | null | undefined
): string | null => {
  if (!active || conditioning !== 'single-procedure-population') {
    return null;
  }

  return eligibleExcludedCaption(retainedCases, excludedCombinedCases);
};

/**
 * Decorates a generic metric tile with CPT-conditioning behavior while a CPT filter is active:
 * - `no-cpt` metrics are greyed/disabled with an explanation.
 * - `single-procedure-population` metrics that carry an excluded-count render an
 *   "N combined cases excluded" label.
 * - `membership` metrics (and every metric when no CPT filter is active) render unchanged,
 *   with no wrapping element so the tile layout is untouched.
 */
const MetricCptConditioning: FC<MetricCptConditioningProps> = ({
  conditioning,
  excludedCombinedCases,
  retainedCases,
  active,
  children,
}) => {
  const classes = useStyles();

  if (!active) {
    return <>{children}</>;
  }

  if (conditioning === 'no-cpt') {
    return (
      <Tooltip title={NO_CPT_EXPLANATION} arrow>
        <div className={classes.wrapper}>
          <div className={classes.disabled}>{children}</div>
          <Typography variant="caption" color="textSecondary" className={classes.note}>
            Not conditioned by CPT (room-interval metric)
          </Typography>
        </div>
      </Tooltip>
    );
  }

  const caption = cptConditioningCaption(active, conditioning, retainedCases, excludedCombinedCases);

  if (caption !== null) {
    return (
      <div className={classes.wrapper}>
        {children}
        <Typography variant="caption" color="textSecondary" className={classes.note}>
          {caption}
        </Typography>
      </div>
    );
  }

  return <>{children}</>;
};

interface CptEligibleCaptionProps {
  /** Whether a CPT filter is currently active (non-empty code selection). */
  active: boolean;
  /**
   * Eligible single-procedure population under the active CPT filter (shared upper bound across the
   * table's single-procedure-population metrics, not any one metric's true `n`).
   */
  retainedCases?: number | null;
  /** Combined cases excluded from the single-procedure population under the active CPT filter. */
  excludedCombinedCases?: number | null;
  /** Optional style override for the caption element. */
  className?: string;
  /**
   * Optional caption text already derived by the caller (e.g. via `eligibleExcludedCaption`) that,
   * when provided, is rendered as-is instead of being recomputed from `retainedCases` /
   * `excludedCombinedCases`. Lets a caller that already gates on the computed caption (to decide
   * whether to render this component's wrapper at all) avoid deriving the same string twice.
   * Undefined (the default) preserves the original recompute-from-counts behavior.
   */
  precomputedCaption?: string | null;
}

/**
 * Table / chart-summary caption for the CPT-conditioning eligible-vs-excluded split. Unlike
 * `MetricCptConditioning` (which decorates an individual metric tile by conditioning class), this
 * renders a single caption sourced from a utilization tab's TOTALS row or a GroupStatistics donut,
 * where only the aggregate counts are available. Renders nothing when no CPT filter is active or
 * when neither count is present (older backend / non-conditioned surface).
 */
export const CptEligibleCaption: FC<CptEligibleCaptionProps> = ({
  active,
  retainedCases,
  excludedCombinedCases,
  className,
  precomputedCaption,
}) => {
  const classes = useStyles();

  if (!active) {
    return null;
  }

  const caption =
    precomputedCaption !== undefined
      ? precomputedCaption
      : eligibleExcludedCaption(retainedCases, excludedCombinedCases);

  if (caption === null) {
    return null;
  }

  return (
    <Typography variant="caption" color="textSecondary" className={className ?? classes.note}>
      {caption}
    </Typography>
  );
};

const useStyles = makeStyles(theme => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    flex: 1,
  },
  disabled: {
    opacity: 0.4,
    pointerEvents: 'none',
  },
  note: {
    display: 'block',
  },
}));

export default MetricCptConditioning;
